import * as XLSX from 'xlsx-js-style';
import type { SubprocessOption } from '../select/SubprocessCells';

// Generic export for the two OSSubprocessEntriesTable cards (InProcess and Параметри за
// монтажа) — same shape (Subprocess.{inProcess,montageParams}), so one util serves both.
// Mirrors osMaterialnoExcel.ts exactly: same library, same header/cell styles, same
// safeFilenamePart, same autofilter + !cols + style loops.

interface ExportArgs {
  subprocesses: SubprocessOption[];
  values: Record<string, string>;
  entriesKey: 'inProcess' | 'montageParams';
  entriesLabel: string;
  sheetName: string;
  filenamePrefix: string;
  mainIdent?: string;
  mainName?: string;
}

const headerStyle = {
  fill: { fgColor: { rgb: 'D9D9D9' } },
  font: { bold: true, color: { rgb: '000000' } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: '999999' } },
    bottom: { style: 'thin', color: { rgb: '999999' } },
    left: { style: 'thin', color: { rgb: '999999' } },
    right: { style: 'thin', color: { rgb: '999999' } },
  },
};

const cellStyle = {
  alignment: { vertical: 'center', wrapText: true },
  border: {
    top: { style: 'thin', color: { rgb: 'DDDDDD' } },
    bottom: { style: 'thin', color: { rgb: 'DDDDDD' } },
    left: { style: 'thin', color: { rgb: 'DDDDDD' } },
    right: { style: 'thin', color: { rgb: 'DDDDDD' } },
  },
};

const safeFilenamePart = (value?: string) =>
  String(value || 'normativ')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 80);

export function downloadOSSubprocessEntriesExcel({
  subprocesses,
  values,
  entriesKey,
  entriesLabel,
  sheetName,
  filenamePrefix,
  mainIdent = '',
  mainName = '',
}: ExportArgs) {
  const data: Record<string, string>[] = [];

  subprocesses.forEach((subprocess) => {
    const entries = subprocess[entriesKey];
    if (!entries || entries.length === 0) return;

    const processDefinition =
      subprocess && typeof subprocess.processDefinition === 'object'
        ? subprocess.processDefinition
        : null;

    entries.forEach((entry, i) => {
      data.push({
        'Главна шифра': mainIdent,
        'Главен назив': mainName,
        'Тип на процес': processDefinition?.name || '',
        'Подпроцес': subprocess.name || '',
        'Р.бр.': String(i + 1),
        [entriesLabel]: entry.text || '',
        'Параметри': values[entry._id] ?? '',
      });
    });
  });

  const headers = [
    'Главна шифра',
    'Главен назив',
    'Тип на процес',
    'Подпроцес',
    'Р.бр.',
    entriesLabel,
    'Параметри',
  ];

  const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });
  worksheet['!cols'] = [
    { wch: 16 },
    { wch: 32 },
    { wch: 24 },
    { wch: 18 },
    { wch: 24 },
    { wch: 8 },
    { wch: 44 },
    { wch: 28 },
  ];
  worksheet['!autofilter'] = { ref: XLSX.utils.encode_range(XLSX.utils.decode_range(worksheet['!ref'] || 'A1:H1')) };

  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:H1');
  for (let c = range.s.c; c <= range.e.c; c += 1) {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c });
    if (worksheet[cellRef]) worksheet[cellRef].s = headerStyle;
  }
  for (let r = 1; r <= range.e.r; r += 1) {
    for (let c = range.s.c; c <= range.e.c; c += 1) {
      const cellRef = XLSX.utils.encode_cell({ r, c });
      if (worksheet[cellRef]) worksheet[cellRef].s = cellStyle;
    }
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(
    workbook,
    `${filenamePrefix}_${safeFilenamePart(mainIdent)}_${new Date().toISOString().split('T')[0]}.xlsx`
  );
}
